import {
  Audience,
  Campaign,
  Status_Enum as CampaignStatus,
  CampaignType_Enum as CampaignType,
  GetCampaignsSummaryRequest,
  ListCampaignsRequest,
  ListListsRequest,
  ListListsResponse,
  Segment,
} from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { QueryKey, useMutation, UseMutationOptions } from 'react-query';
import { useOrgData } from '@frontend/get-org-data';
import { HTTPBoundSchemaMethod, SchemaIO } from '@frontend/schema';
import { keys } from './queries';
import { SchemaBulkMessagingService } from './service';
import { sortCampaigns } from './utils/sort-campaigns';
import { useBulkMessagingQueryHelpers } from './utils/use-query-helpers';

// TODO: Replace SchemaMutationOptions with helper type from the query helper library. Or just remove it completely.
type SchemaMutationOptions<T extends HTTPBoundSchemaMethod<any>> = UseMutationOptions<
  SchemaIO<T>['output'],
  unknown,
  SchemaIO<T>['input'],
  unknown
>;

export const useCreateCampaign = (
  campaignType: CampaignType,
  options: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['CreateCampaign']> = {}
) => {
  const { orgId } = useOrgData();
  const { queryClient, findFirstQueryKey, invalidateQueriesByCampaign } = useBulkMessagingQueryHelpers();
  const summaryQueryKey =
    findFirstQueryKey({
      campaignType,
      key: keys.base.getCampaignsSummary,
      orgId,
      validator: isGetCampaignsSummaryRequest,
    }) ?? keys.base.getCampaignsSummary;
  const draftsQueryKey =
    findFirstQueryKey({
      key: keys.base.listDraftCampaigns,
      orgId,
      validator: isListCampaignsRequest,
    }) ?? keys.base.listDraftCampaigns;

  return useMutation({
    mutationKey: 'create-campaign',
    mutationFn: SchemaBulkMessagingService.CreateCampaign,

    onMutate: (request) => {
      const { isDraft, isHiddenDraft } = getDraftStatuses(request.campaign);
      const queryKey = isDraft ? draftsQueryKey : summaryQueryKey;
      const previousCampaigns = queryClient.getQueryData<Campaign[]>(queryKey);

      queryClient.cancelQueries(queryKey);
      if (isHiddenDraft) return { previousCampaigns };

      const updatedCampaigns = sortCampaigns([...(previousCampaigns ?? []), request.campaign]);
      queryClient.setQueryData(queryKey, updatedCampaigns);
      return { previousCampaigns };
    },

    onError: (_error, request, context: unknown) => {
      const { isDraft } = getDraftStatuses(request.campaign);
      const queryKey = isDraft ? draftsQueryKey : summaryQueryKey;
      const { previousCampaigns } = context as { previousCampaigns: Campaign[] };
      queryClient.setQueryData(queryKey, previousCampaigns);
    },

    onSettled: (response, _error, request) => {
      const { isDraft, isHiddenDraft } = getDraftStatuses(request.campaign);
      if (isHiddenDraft) return;

      const queryKey = isDraft ? keys.base.listDraftCampaigns : keys.base.getCampaignsSummary;
      queryClient.invalidateQueries(queryKey);

      if (!response) return;

      invalidateQueriesByCampaign([keys.base.getAudience, keys.base.getCampaignUsage], response.campaignId);
      queryClient.setQueryData(keys.getCampaign({ campaignId: response.campaignId, orgId: request.orgId }), response);
    },

    ...options,
  });
};

export const useCreateSegment = ({
  onMutate,
  onSuccess,
  ...options
}: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['CreateSegment']> = {}) => {
  const { queryClient, cancelQueriesByCampaign, invalidateQueriesByCampaign } = useBulkMessagingQueryHelpers();

  return useMutation({
    mutationKey: 'create-segment',
    mutationFn: SchemaBulkMessagingService.CreateSegment,

    onMutate: (request) => {
      cancelQueriesByCampaign(
        [keys.base.getAudience, keys.base.getCampaignUsage, keys.base.listSegments],
        request.campaignId
      );

      return onMutate?.(request);
    },

    onSuccess: (response, request, context) => {
      invalidateQueriesByCampaign(
        [keys.base.getAudience, keys.base.getCampaignUsage, keys.base.listRecipients, keys.base.listSegments],
        request.campaignId
      );
      queryClient.invalidateQueries(keys.base.getCampaignsSummary);

      // Optimistically update the segments. The earliest we can do this is right after the segment is created and we know its count.
      const queryKey = keys.listSegments({ campaignId: request.campaignId, orgId: request.orgId });
      const previousSegments = queryClient.getQueryData<Segment[]>(queryKey);
      const updatedSegments: Segment[] = [...(previousSegments ?? []), response];
      queryClient.setQueryData(queryKey, updatedSegments);

      onSuccess?.(response, request, context);
    },

    ...options,
  });
};

export const useDeleteCampaign = (
  isDraft = false,
  options: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['DeleteCampaign']> = {}
) => {
  const { queryClient, removeQueriesByCampaign } = useBulkMessagingQueryHelpers();
  const queryKey = isDraft ? keys.base.listDraftCampaigns : keys.base.getCampaignsSummary;

  return useMutation({
    mutationKey: 'delete-campaign',
    mutationFn: SchemaBulkMessagingService.DeleteCampaign,

    onMutate: (request) => {
      queryClient.cancelQueries(queryKey);

      // Delete campaign from the campaigns list
      const previousCampaigns = queryClient.getQueriesData<Campaign[]>(queryKey);
      queryClient.setQueriesData<Campaign[] | undefined>({ queryKey }, (campaigns) =>
        campaigns?.filter((campaign) => campaign.campaignId !== request.campaignId)
      );

      return { previousCampaigns };
    },

    onError: (_error, _request, context: unknown) => {
      const { previousCampaigns } = context as { previousCampaigns: [QueryKey, Campaign[]][] };
      queryClient.setQueriesData(queryKey, previousCampaigns);
    },

    onSuccess: (_response, request) => {
      removeQueriesByCampaign(
        [
          keys.base.getAudience,
          keys.base.getCampaign,
          keys.base.getCampaignUsage,
          keys.base.listRecipients,
          keys.base.listSegments,
        ],
        request.campaignId
      );
      queryClient.invalidateQueries(queryKey);
    },

    ...options,
  });
};

export const useDeleteSegment = (
  options: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['DeleteSegment']> = {}
) => {
  const { queryClient, cancelQueriesByCampaign, invalidateQueriesByCampaign } = useBulkMessagingQueryHelpers();

  return useMutation({
    mutationKey: 'delete-segment',
    mutationFn: SchemaBulkMessagingService.DeleteSegment,

    onMutate: (request) => {
      cancelQueriesByCampaign([keys.base.listSegments], request.campaignId);

      // Delete segment from the campaign
      const queryKey = keys.listSegments({ campaignId: request.campaignId, orgId: request.orgId });
      const previousSegments = queryClient.getQueryData<Segment[]>(queryKey);
      const updatedSegments = previousSegments?.filter((segment) => segment.segmentId !== request.segmentId);
      queryClient.setQueryData(queryKey, updatedSegments);

      return { previousSegments };
    },

    onError: (_error, request, context: unknown) => {
      const { previousSegments } = context as { previousSegments?: Segment[] };
      queryClient.setQueryData(
        keys.listSegments({ campaignId: request.campaignId, orgId: request.orgId }),
        previousSegments
      );
    },

    onSettled: (_response, _error, request) => {
      invalidateQueriesByCampaign(
        [keys.base.getAudience, keys.base.getCampaignUsage, keys.base.listRecipients, keys.base.listSegments],
        request.campaignId
      );
    },

    ...options,
  });
};

type UpdateCampaignMutationContext = {
  previousCampaign?: Campaign;
  previousSummaryCampaigns: Campaign[];
  previousDraftCampaigns: Campaign[];
};

export const useUpdateCampaign = (
  campaignType: CampaignType,
  options: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['UpdateCampaign']> = {}
) => {
  const { orgId } = useOrgData();
  const { queryClient, findFirstQueryKey, invalidateQueriesByCampaign } = useBulkMessagingQueryHelpers();
  const summaryQueryKey =
    findFirstQueryKey({
      campaignType,
      key: keys.base.getCampaignsSummary,
      orgId,
      validator: isGetCampaignsSummaryRequest,
    }) ?? keys.base.getCampaignsSummary;
  const draftsQueryKey =
    findFirstQueryKey({
      key: keys.base.listDraftCampaigns,
      orgId,
      validator: isListCampaignsRequest,
    }) ?? keys.base.listDraftCampaigns;

  return useMutation({
    mutationKey: 'update-campaign',
    mutationFn: SchemaBulkMessagingService.UpdateCampaign,

    onMutate: (request) => {
      queryClient.cancelQueries(summaryQueryKey);
      queryClient.cancelQueries(draftsQueryKey);

      const campaignQueryKey = keys.getCampaign({ campaignId: request.campaign.campaignId, orgId: request.orgId });
      const previousCampaign = queryClient.getQueryData<Campaign>(campaignQueryKey);
      const summaryCampaigns = queryClient.getQueryData<Campaign[]>(summaryQueryKey) ?? [];
      const draftCampaigns =
        (queryClient.getQueryData<{ draftCampaigns: Campaign[] }>(draftsQueryKey) ?? {}).draftCampaigns ?? [];

      let updatedSummaryCampaigns: Campaign[];
      let updatedDraftCampaigns: Campaign[];

      const { isDraft: isRequestDraft } = getDraftStatuses(request.campaign);

      // Drafts
      if (isRequestDraft) {
        const hasExistingDraft = draftCampaigns.find((campaign) => campaign.campaignId === request.campaign.campaignId);
        if (hasExistingDraft) {
          // Update the existing campaign in the drafts list
          updatedDraftCampaigns = draftCampaigns.map((draftCampaign) =>
            draftCampaign.campaignId === request.campaign.campaignId ? request.campaign : draftCampaign
          );
        } else {
          // Add the campaign to the drafts list if it's not found
          updatedDraftCampaigns = [...draftCampaigns, request.campaign];
        }

        // Remove the draft campaign from the summary list
        updatedSummaryCampaigns = summaryCampaigns.filter(
          (campaign) => campaign.campaignId !== request.campaign.campaignId
        );
      }
      // Scheduled
      else {
        const hasExistingCampaign = summaryCampaigns.find(
          (campaign) => campaign.campaignId === request.campaign.campaignId
        );
        if (hasExistingCampaign) {
          // Update the existing campaign in the summary list
          updatedSummaryCampaigns = summaryCampaigns.map((campaign) =>
            campaign.campaignId === request.campaign.campaignId ? request.campaign : campaign
          );
        } else {
          // Add the campaign to the summary list
          updatedSummaryCampaigns = [...summaryCampaigns, request.campaign];
        }

        // Remove the campaign from the drafts list
        updatedDraftCampaigns = draftCampaigns.filter(
          (draftCampaign) => draftCampaign.campaignId !== request.campaign.campaignId
        );
      }

      queryClient.setQueryData(campaignQueryKey, request.campaign);
      queryClient.setQueryData(summaryQueryKey, sortCampaigns(updatedSummaryCampaigns));
      queryClient.setQueryData(draftsQueryKey, sortCampaigns(updatedDraftCampaigns));

      return {
        previousCampaign,
        previousSummaryCampaigns: summaryCampaigns,
        previousDraftCampaigns: draftCampaigns,
      } as UpdateCampaignMutationContext;
    },

    onError: (_error, request, context: unknown) => {
      const { previousCampaign, previousSummaryCampaigns, previousDraftCampaigns } =
        context as UpdateCampaignMutationContext;
      const campaignQueryKey = keys.getCampaign({ campaignId: request.campaign.campaignId, orgId: request.orgId });
      queryClient.setQueryData(campaignQueryKey, previousCampaign);
      queryClient.setQueryData(summaryQueryKey, previousSummaryCampaigns);
      queryClient.setQueryData(draftsQueryKey, previousDraftCampaigns);
    },

    onSettled: (_response, _error, request) => {
      invalidateQueriesByCampaign([keys.base.getCampaign], request.campaign.campaignId);
      queryClient.invalidateQueries(draftsQueryKey);
      queryClient.invalidateQueries(summaryQueryKey);
    },

    ...options,
  });
};

type ListInfiniteQuery = { pages: ListListsResponse[]; pageParams: string[] };
export const useCreateList = (listListsRequest: ListListsRequest) => {
  const { queryClient, invalidateQueriesByType } = useBulkMessagingQueryHelpers();
  const listListsQueryKey = keys.listLists(listListsRequest);

  return useMutation({
    mutationKey: 'createList',
    mutationFn: SchemaBulkMessagingService.CreateList,

    onSuccess: (response, request) => {
      const previousList = queryClient.getQueryData<ListInfiniteQuery>(listListsQueryKey);
      invalidateQueriesByType([keys.base.listLists, keys.base.getList], listListsRequest.types);
      queryClient.setQueryData(listListsQueryKey, () => {
        const pages = [
          { lists: [{ ...response, recipients: request.list.recipients }] },
          ...(previousList?.pages ?? []),
        ];
        return { pages, pageParams: previousList?.pageParams ?? [] };
      });
    },
  });
};

export const useDeleteList = (listListsRequest: ListListsRequest) => {
  const { queryClient, invalidateQueriesByType } = useBulkMessagingQueryHelpers();
  const listListsQueryKey = keys.listLists(listListsRequest);

  return useMutation({
    mutationKey: 'deleteList',
    mutationFn: SchemaBulkMessagingService.DeleteList,

    onMutate: (request) => {
      queryClient.cancelQueries(listListsQueryKey);
      const previousList = queryClient.getQueryData<ListInfiniteQuery>(listListsQueryKey);
      queryClient.setQueryData(listListsQueryKey, () => {
        const pages =
          previousList?.pages?.map((page) => ({
            lists: page.lists.filter((list) => list.listId !== request.listId),
          })) ?? [];
        return { pages, pageParams: previousList?.pageParams ?? [] };
      });
      return { previousList };
    },

    onSuccess: (_response, _request) => {
      invalidateQueriesByType([keys.base.listLists, keys.base.getList], listListsRequest.types);
    },

    onError: (_error, _request, context: unknown) => {
      const { previousList } = context as { previousList: ListInfiniteQuery };
      queryClient.setQueryData(listListsQueryKey, previousList);
    },
  });
};

export const useUpdateList = (listListsRequest: ListListsRequest) => {
  const { queryClient, invalidateQueriesByType } = useBulkMessagingQueryHelpers();
  const listListsQueryKey = keys.listLists(listListsRequest);

  return useMutation({
    mutationKey: 'updateList',
    mutationFn: SchemaBulkMessagingService.UpdateList,

    onMutate: (request) => {
      queryClient.cancelQueries(listListsQueryKey);
      const previousList = queryClient.getQueryData<ListInfiniteQuery>(listListsQueryKey);
      queryClient.setQueryData(listListsQueryKey, () => {
        const pages =
          previousList?.pages?.map((page) => ({
            lists: page.lists.map((list) => (list.listId === request.list.listId ? request.list : list)),
          })) ?? [];
        return { pages, pageParams: previousList?.pageParams ?? [] };
      });
      return { previousList };
    },

    onSuccess: (response, _request) => {
      const previousList = queryClient.getQueryData<ListInfiniteQuery>(listListsQueryKey);
      invalidateQueriesByType([keys.base.listLists, keys.base.getList], listListsRequest.types);
      queryClient.setQueryData(listListsQueryKey, () => {
        const pages =
          previousList?.pages?.map((page) => ({
            lists: page.lists.map((list) => (list.listId === response.listId ? response : list)),
          })) ?? [];
        return { pages, pageParams: previousList?.pageParams ?? [] };
      });
    },

    onError: (_error, _request, context: unknown) => {
      const { previousList } = context as { previousList: ListInfiniteQuery };
      queryClient.setQueryData(listListsQueryKey, previousList);
    },
  });
};

type LimitAudienceMutationContext = {
  previousAudience?: Audience;
  previousCampaign?: Campaign;
  recipients?: number;
};

export const useLimitAudience = (
  options: SchemaMutationOptions<(typeof SchemaBulkMessagingService)['LimitAudience']> = {}
) => {
  const { queryClient, cancelQueriesByCampaign, invalidateQueriesByCampaign } = useBulkMessagingQueryHelpers();

  return useMutation({
    mutationKey: 'limit-audience',
    mutationFn: SchemaBulkMessagingService.LimitAudience,

    onMutate: (request) => {
      const audienceQueryKey = keys.getAudience({ campaignId: request.campaignId, orgId: request.orgId });
      const campaignQueryKey = keys.getCampaign({ campaignId: request.campaignId, orgId: request.orgId });

      cancelQueriesByCampaign([keys.base.getAudience, keys.base.getCampaign], request.campaignId);

      const previousAudience = queryClient.getQueryData<Audience>(audienceQueryKey);
      const previousCampaign = queryClient.getQueryData<Campaign>(campaignQueryKey);

      const recipients = request.limits?.reduce((acc, limit) => acc + (limit.count || 0), 0);
      const newCampaign = { ...previousCampaign, recipients };

      queryClient.setQueryData(campaignQueryKey, newCampaign);
      queryClient.setQueryData(audienceQueryKey, { ...previousAudience, counts: request.limits });

      return { previousAudience, previousCampaign, recipients };
    },

    onError: (_error, request, context: unknown) => {
      const { previousAudience, previousCampaign } = context as LimitAudienceMutationContext;
      queryClient.setQueryData(
        keys.getAudience({ campaignId: request.campaignId, orgId: request.orgId ?? '' }),
        previousAudience
      );
      queryClient.setQueryData(
        keys.getCampaign({ campaignId: request.campaignId, orgId: request.orgId ?? '' }),
        previousCampaign
      );
    },

    onSettled: (_response, _error, request, context: unknown) => {
      const typedContext = context as LimitAudienceMutationContext;
      const isDraft =
        typedContext.previousCampaign?.currentStatus === CampaignStatus.DRAFT ||
        typedContext.previousCampaign?.currentStatus === CampaignStatus.HIDDEN_DRAFT;
      const listQueryKey = isDraft ? keys.base.listDraftCampaigns : keys.base.getCampaignsSummary;

      const previousList = queryClient.getQueryData<Campaign[]>(listQueryKey);
      const newList = previousList?.map((campaign) => {
        if (campaign.campaignId === request.campaignId) {
          return { ...campaign, recipients: typedContext?.recipients };
        }
        return campaign;
      });

      queryClient.setQueryData(listQueryKey, newList);
      invalidateQueriesByCampaign(
        [keys.base.getAudience, keys.base.getCampaign, keys.base.getCampaignUsage],
        request.campaignId
      );
    },

    ...options,
  });
};

const getDraftStatuses = (campaign: Campaign) => {
  const isDraft = campaign.currentStatus === CampaignStatus.DRAFT;
  const isHiddenDraft = campaign.currentStatus === CampaignStatus.HIDDEN_DRAFT;
  return { isDraft, isHiddenDraft };
};

const isListCampaignsRequest = (value: any): value is ListCampaignsRequest =>
  !!(value && value.locationIds && value.orgId && value.sort && value.year);

const isGetCampaignsSummaryRequest = (value?: any): value is GetCampaignsSummaryRequest =>
  !!(value && value.locationIds && value.orgId && value.types && value.year);
